import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useEvents } from "@/hooks/useEvents";
import { useVehicles } from "@/hooks/useVehicles";
import { useUpdateRecordingSession } from "@/hooks/useTeams";
import type { Team, TeamRecordingSummary } from "@/types";
import { formatUserName } from "@/utils/userName";

type TeamMembers = NonNullable<Team["members"]>;

interface RecordingEditModalProps {
  teamId: string;
  recording: TeamRecordingSummary;
  members: TeamMembers;
  onClose: () => void;
}

interface Option {
  id: string;
  label: string;
}

export default function RecordingEditModal({
  teamId,
  recording,
  members,
  onClose,
}: RecordingEditModalProps) {
  const { t } = useTranslation("pages");
  const { data: eventsData } = useEvents();
  const events = Array.isArray(eventsData) ? eventsData : eventsData?.events;
  const { data: vehicles } = useVehicles();
  const updateSession = useUpdateRecordingSession(teamId);

  const [eventId, setEventId] = useState(recording.session.event.id);
  const [vehicleId, setVehicleId] = useState(recording.session.vehicle.id);
  const [driverId, setDriverId] = useState(recording.session.driver.id);

  const eventOptions: Option[] = useMemo(() => {
    const list = (events ?? []).map((event) => ({
      id: event.id,
      label: event.name,
    }));
    if (!list.some((option) => option.id === eventId)) {
      list.unshift({ id: eventId, label: recording.session.event.name });
    }
    return list;
  }, [events, eventId, recording]);
  const vehicleOptions: Option[] = useMemo(() => {
    const list = (vehicles ?? []).map((vehicle) => ({
      id: vehicle.id,
      label: vehicle.name || `${vehicle.year} ${vehicle.maker} ${vehicle.model}`,
    }));
    if (!list.some((option) => option.id === vehicleId)) {
      list.unshift({ id: vehicleId, label: recording.session.vehicle.name });
    }
    return list;
  }, [vehicles, vehicleId, recording]);
  const driverOptions: Option[] = useMemo(
    () =>
      members
        .filter((member) => member.status === "active")
        .map((member) => ({
          id: member.userId,
          label: formatUserName(member.user),
        })),
    [members],
  );

  const selectClass =
    "w-full rounded-md border border-secondary-300 bg-white px-2 py-1.5 text-sm";

  const labelWrapper = (labelKey: string, node: ReactNode) => (
    <label className="block text-sm">
      <span className="mb-1 block text-secondary-600">{t(labelKey)}</span>
      {node}
    </label>
  );

  const handleSubmit = () => {
    updateSession.mutate(
      {
        sessionId: recording.session.id,
        data: { eventId, vehicleId, userId: driverId },
      },
      { onSuccess: onClose },
    );
  };

  const error = updateSession.error as
    | { response?: { data?: { error?: { code?: string } } } }
    | null;
  const errorCode = error?.response?.data?.error?.code;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-4 shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="mb-3 text-lg font-semibold">
          {t("teams.recordings.editTitle")}
        </h2>

        <div className="space-y-3">
          {labelWrapper(
            "teams.recordings.event",
            <select
              aria-label={t("teams.recordings.event")}
              className={selectClass}
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
            >
              {eventOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>,
          )}
          {labelWrapper(
            "teams.recordings.car",
            <select
              aria-label={t("teams.recordings.car")}
              className={selectClass}
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
            >
              {vehicleOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>,
          )}
          {labelWrapper(
            "teams.recordings.driver",
            <select
              aria-label={t("teams.recordings.driver")}
              className={selectClass}
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
            >
              {driverOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>,
          )}
        </div>

        {error && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {errorCode === "DRIVER_NOT_TEAM_MEMBER"
              ? t("teams.recordings.driverNotTeamMember")
              : errorCode === "EVENT_NOT_ACCESSIBLE"
                ? t("teams.recordings.eventNotAccessible")
                : errorCode === "VEHICLE_NOT_OWNED"
                  ? t("teams.recordings.vehicleNotOwned")
                  : t("teams.recordings.saveFailed")}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-secondary-300 px-3 py-1.5 text-sm"
            onClick={onClose}
          >
            {t("teams.recordings.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary px-3 py-1.5 text-sm"
            disabled={updateSession.isPending}
            onClick={handleSubmit}
          >
            {t("teams.recordings.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
